import { useState } from "react";
import { Pet } from "../../../common/types";
import { Meter } from "../utils/Meter";
import { DarkMeter } from "../utils/DarkMeter";
import {
  formatTimerCardTime,
  getTrainingStartAndEndTime,
  getPetTypeFlags,
} from "../../helpers/helpers";
import { serverURL } from "../../helpers/utils";

export function PetTimerCard({
  pet,
  walletAddress,
  isDarkMode,
}: {
  pet: Pet;
  walletAddress: string;
  isDarkMode: boolean;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isNextGacha, setIsNextGacha] = useState(true);

  const toggleTooltip = () => setShowTooltip(!showTooltip);

  const { trainingStartSec, trainingEndSec } = getTrainingStartAndEndTime(pet);

  const { isZFren, isDMonkey, isGDog, isFPanda, isEPepe } = getPetTypeFlags(pet.petType);
  const timeUntilStarving = formatTimerCardTime(pet.timeUntilStarving, "starving");
  const timeUntilAttack = formatTimerCardTime(pet.timeUntilAttack, "attack");
  const timeUntilWheel = formatTimerCardTime(pet.timeUntilWheel, "wheel");
  const timeUntilShieldExpires = formatTimerCardTime(pet.shieldExpires, "shield");
  const timeTrainingEnds = formatTimerCardTime(trainingEndSec, "training");
  const availableBonks = pet.availableBonks;
  const passHolder = pet.passHolder;
  const petStatus = pet.status;
  const staker = pet.stakingPerks;
  const MeterComponent = isDarkMode ? DarkMeter : Meter;

  let hibernating = false;
  if (petStatus === 5) {
    hibernating = true;
  }

  const toggleGachaValue = () => setIsNextGacha(!isNextGacha);

  return (
    <div
      className={`mb-2 ml-4 mr-4 mt-2 flex h-[15.75rem] w-[21.5rem] flex-row rounded-lg border-2 p-3 ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]" : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"}`}
    >
      <div className="relative flex w-[35%] flex-col">
        <div
          className={`h-16 w-16 overflow-hidden rounded-lg border-2 ${isDarkMode ? "border-darkgreen bg-green" : "border-brown bg-green"}`}
        >
          <span
            className={`borde absolute ml-[1.85rem] -translate-y-1/3 translate-x-1/3 transform rounded-full border px-[.4rem] py-[.1rem] text-xs ring-1 ring-inset ${isDarkMode ? "border-darkgreen bg-darkblue text-white ring-darkgreen [box-shadow:inset_0_-2px_#37b764]" : "border-brown bg-green text-brown ring-brown [box-shadow:inset_0_-2px_#342e2e]"}`}
          >
            lv. {pet.level}
          </span>
          <a href={`https://frenpet.xyz/pet/${pet.id}`} target="_blank" rel="noreferrer">
            <img
              src={`${serverURL}/svgs/${pet.svgPath}`}
              alt={"no pet img"}
              className={`object-cover ${
                isZFren
                  ? "ml-[.1rem] h-[3.5rem] w-[3.5rem]"
                  : isDMonkey || isGDog || isFPanda
                    ? "ml-[.65rem] mt-2 h-[2.5rem] w-[2.5rem]"
                    : ""
              } ${isEPepe ? "mix-blend-multiply" : ""}`}
            />
          </a>
        </div>
        {hibernating && (
          <span className="absolute ml-[.2rem] mt-[3rem]">
            <img src="/imgs/hibernated.svg" alt="badge" className="h-[.8rem] w-[.8rem]" />
          </span>
        )}
        {!hibernating && passHolder && (
          <span className="absolute ml-[.2rem] mt-[3rem]">
            <img src="/imgs/passBadge.png" alt="badge" className="h-[.8rem] w-[.8rem]" />
          </span>
        )}
        {!hibernating && !passHolder && staker && (
          <span className="absolute ml-[.3rem] mt-[3rem]">
            <img src="/imgs/frog.svg" alt="frog" className="h-[.8rem] w-[.8rem]" />
          </span>
        )}
        <div className="ml-1 mt-2 flex flex-col items-start">
          <h2 className="text-md">id #{pet.id}</h2>
          <h2 className="text-md mt-4 max-w-24 overflow-hidden text-ellipsis whitespace-nowrap">
            {pet.name}
          </h2>
          <h2 className="text-md mt-3 flex max-w-24 overflow-hidden text-ellipsis whitespace-nowrap">
            <img
              src={isDarkMode ? "/imgs/piggy-bankDark.svg" : "/imgs/piggy-bank.svg"}
              alt=""
              className="mr-1 mt-[.7rem] h-4 w-4"
            />
            <div className="mt-2"> {pet.score.toFixed(0)} </div>
          </h2>
          <h2
            className="text-md mt-4 flex max-w-24 overflow-hidden text-ellipsis whitespace-nowrap"
            onClick={toggleGachaValue}
          >
            {pet.availableGacha > 0 && (
              <span
                className={`absolute -translate-y-[30%] translate-x-[10%] transform rounded-full border px-[.35rem] py-[.05rem] text-xs ring-1 ring-inset ${isDarkMode ? "border-darkgreen bg-darkblue text-white ring-darkgreen [box-shadow:inset_0_-2px_#37b764]" : "border-brown bg-green text-brown ring-brown [box-shadow:inset_0_-2px_#342e2e]"}`}
              >
                {pet.availableGacha}
              </span>
            )}
            <img
              src={isDarkMode ? "/imgs/slotDark.svg" : "/imgs/slot.svg"}
              alt=""
              className="mr-1 mt-[.47rem] h-4 w-4"
            />
            <div className="mt-1">
              {" "}
              {isNextGacha ? pet.nextGacha.toFixed(0) : pet.gachaEfficiency?.toFixed(2)}{" "}
            </div>
          </h2>
        </div>
      </div>
      <div className={`mr-3 border ${isDarkMode ? "border-darkgreen" : "border-brown"} `}></div>
      <div className="relative flex w-[65%] flex-col justify-start">
        <div className="flex h-full w-full flex-col items-start justify-between">
          {showTooltip && (
            <div
              className={`top-50 absolute z-20 mt-2 -translate-x-1/2 transform rounded border-2  px-2 py-1 ${isDarkMode ? "border-darkgreen bg-darkblue [box-shadow:inset_0_-2px_#37b764]" : "border-brown bg-white text-brown [box-shadow:inset_0_-2px_#342e2e]"}`}
            >
              <div className="flex">
                <img
                  src={isDarkMode ? "/imgs/skullDark.svg" : "/imgs/skull.svg"}
                  alt=""
                  className=" ml-1 mr-1 mt-[0.22rem] h-4 w-4"
                />
                • {timeUntilStarving}
              </div>
              <div className="mt-2 flex">
                <img
                  src={isDarkMode ? "/imgs/swordDark.svg" : "/imgs/sword.svg"}
                  alt=""
                  className=" z-10 mt-[0.05rem] h-6 w-6 scale-x-[-1] transform"
                />
                • {timeUntilAttack}
              </div>
              <div className="mt-2 flex">
                <img
                  src={isDarkMode ? "/imgs/boltDark.svg" : "/imgs/bolt.svg"}
                  alt=""
                  className=" z-10 mt-[0.05rem] h-5 w-5 "
                />
                • {availableBonks} bonks
              </div>
              <div className="mt-2 flex">
                <img
                  src={isDarkMode ? "/imgs/wheelspinDark.svg" : "/imgs/wheelspin.svg"}
                  alt=""
                  className=" mr-1 mt-[0.11rem] h-5 w-5 scale-x-[-1] transform"
                />
                • {timeUntilWheel}
              </div>
              <div className="mt-2 flex">
                <img
                  src={isDarkMode ? "/imgs/shield-solidDark.svg" : "/imgs/shield-solid.svg"}
                  alt=""
                  className=" mr-1 mt-1 h-4 w-5 scale-x-[-1] transform"
                />
                • {timeUntilShieldExpires}
              </div>
              <div className="mt-2 flex">
                <img
                  src={isDarkMode ? "/imgs/weightDark.svg" : "/imgs/weight.svg"}
                  alt=""
                  className=" mr-1 mt-1 h-4 w-5 scale-x-[-1] transform"
                />
                • {timeTrainingEnds}
              </div>
            </div>
          )}
          <MeterComponent
            startSec={pet.timeUntilStarvingStart}
            endSec={pet.timeUntilStarving}
            imgSrc={isDarkMode ? "/imgs/appleDark.svg" : "/imgs/apple.svg"}
            imgClass="h-[1.3rem] w-[1.3rem] ml-[0.12rem]"
            isDepleting={true}
            showTooltip={false}
            walletAddress={walletAddress}
            reverseTooltip={true}
            onClick={toggleTooltip}
            hibernating={hibernating}
            isDarkMode={isDarkMode}
          />

          <MeterComponent
            startSec={pet.lastAttackUsed}
            endSec={pet.timeUntilAttack}
            imgSrc={isDarkMode ? "/imgs/swordDark.svg" : "/imgs/sword.svg"}
            imgClass="h-6 w-6 scale-x-[-1] transform"
            isDepleting={false}
            showTooltip={false}
            walletAddress={walletAddress}
            reverseTooltip={true}
            isDarkMode={isDarkMode}
          />

          <MeterComponent
            startSec={pet.lastWheelSpin}
            endSec={pet.timeUntilWheel}
            imgSrc={isDarkMode ? "/imgs/wheelspinDark.svg" : "/imgs/wheelspin.svg"}
            imgClass="h-6 w-6"
            isDepleting={false}
            showTooltip={false}
            walletAddress={walletAddress}
            reverseTooltip={true}
            isDarkMode={isDarkMode}
          />

          <MeterComponent
            startSec={trainingStartSec}
            endSec={trainingEndSec}
            imgSrc={isDarkMode ? "/imgs/weightDark.svg" : "/imgs/weight.svg"}
            imgClass="h-[1.3rem] w-[1.3rem] ml-[.1rem]"
            isDepleting={false}
            showTooltip={false}
            walletAddress={walletAddress}
            reverseTooltip={true}
            isTraining={pet.isTraining}
            trainingEndSec={trainingEndSec}
            isDarkMode={isDarkMode}
          />

          <MeterComponent
            startSec={pet.shieldStart}
            endSec={pet.shieldExpires}
            imgSrc={isDarkMode ? "/imgs/shield-solidDark.svg" : "/imgs/shield-solid.svg"}
            imgClass="h-[1.3rem] w-[1.3rem] ml-[.1rem]"
            isDepleting={true}
            showTooltip={false}
            walletAddress={walletAddress}
            isDarkMode={isDarkMode}
          />
        </div>
      </div>
    </div>
  );
}
